import React, { useRef, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import { styled } from "@mui/material/styles";
import { PrimaryTooltip } from "../../../Components/StyledComponent";

const SCROLLBAR_WIDTH = "8px";

const ChartContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  gap: "16px",
  height: "416px",
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    width: SCROLLBAR_WIDTH,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D1D1D1",
    borderRadius: "4px",
  },
}));

const LabelsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "180px",
  flexShrink: 0,
  height: "100%",
});

const ChartWrapper = styled(Box)(({ drillDownEnabled }) => ({
  flex: 1,
  minWidth: 0,
  ...(drillDownEnabled && {
    "& .apexcharts-series path": { cursor: "pointer" },
    "& .apexcharts-bar-area": { cursor: "pointer" },
  }),
}));

const FleetBreakdownChartInternal = ({
  categories = [],
  series = [],
  height,
  options: parentOptions = {},
  xAxisTickFormatter,
  minVisibilityHeight = 200,
  xAxisTitle,
  labelPrefix = "fleet-label",
  barGap: barGapProp = 30,
  drillDownEnabled = false,
  fixedBarHeight: fixedBarHeightProp = 16,
}) => {
  const hoveredIndexRef = useRef(null);

  const { xaxisMax, ticks } = useMemo(() => {
    if (!series || series.length === 0)
      return { xaxisMax: 100, ticks: [0, 33, 66, 100] };

    // Check if stacked
    const isStacked = parentOptions?.chart?.stacked;

    // Find max value from data
    let dataMax = 0;
    if (isStacked) {
      // Sum values at each index
      const dataLength = series[0]?.data?.length || 0;
      for (let i = 0; i < dataLength; i++) {
        let sum = 0;
        series.forEach((s) => {
          sum += s.data[i] || 0;
        });
        if (sum > dataMax) dataMax = sum;
      }
    } else {
      // Max of all individual values
      series.forEach((s) => {
        const sMax = Math.max(...(s.data || []));
        if (sMax > dataMax) dataMax = sMax;
      });
    }

    if (dataMax === 0) return { xaxisMax: 100, ticks: [0, 25, 50, 75, 100] };

    // Calculate Nice Scale
    const targetTickCount = 4;
    const roughStep = dataMax / (targetTickCount - 1);

    const calculateNiceStep = (value) => {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;

      let niceNormalized;
      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;

      return niceNormalized * magnitude;
    };

    let niceStep = calculateNiceStep(roughStep);
    niceStep = Math.max(1, niceStep);

    let niceMax = Math.ceil(dataMax / niceStep) * niceStep;

    // Ensure x-axis max is at least one "nice step" greater than dataMax to fit labels
    if (niceMax <= dataMax) {
      niceMax += niceStep;
    }

    const stepCount = Math.round(niceMax / niceStep);
    const generatedTicks = Array.from(
      { length: stepCount + 1 },
      (_, i) => i * niceStep,
    );

    return { xaxisMax: niceMax, ticks: generatedTicks };
  }, [series, parentOptions]);

  const fixedBarHeight = fixedBarHeightProp;
  const barGap = barGapProp;
  const numBars = categories?.length || 0;
  const totalPadding = 0; // Top and bottom padding
  const labelOffset = 0; // Extra padding to push labels down
  const labelHeight = fixedBarHeight + barGap;

  // Calculate content height based on bars
  const contentHeight = numBars * labelHeight + totalPadding;

  // Use dynamic height if provided, else calc based on bars
  let chartHeight = height || contentHeight;
  let extraBottomPadding = 0;

  // If using auto-height and content is too small, add padding to ensure visibility
  if (!height && contentHeight < minVisibilityHeight) {
    extraBottomPadding = minVisibilityHeight - contentHeight;
    chartHeight = minVisibilityHeight;
  }

  const addHoverBackground = useCallback(
    (index, chartContext) => {
      // 1. Clear Chart Background
      if (chartContext) {
        const existingBgs = chartContext.el.querySelectorAll(".hover-row-bg");
        existingBgs.forEach((bg) => bg.remove());
      }

      // 2. Clear Label Backgrounds
      const allLabels = document.querySelectorAll(`[id^="${labelPrefix}-"]`);
      allLabels.forEach((label) => {
        label.style.backgroundColor = "transparent";
      });

      // If index is null (or invalid), we are just clearing
      if (index === null || index === undefined || index < 0) return;

      // Apply New Highlights
      // 1. Chart Background
      if (chartContext) {
        const innerChart = chartContext.el.querySelector(".apexcharts-inner");
        if (innerChart) {
          const chartWidth = innerChart.getBBox().width;
          const hoverHeight = fixedBarHeight + 8;
          const offset = (labelHeight - hoverHeight) / 2;
          const yPos = index * labelHeight + offset + totalPadding / 2;

          const svgNS = "http://www.w3.org/2000/svg";
          const rect = document.createElementNS(svgNS, "rect");
          rect.setAttribute("class", "hover-row-bg");
          rect.setAttribute("x", "0");
          rect.setAttribute("y", yPos);
          rect.setAttribute("width", chartWidth);
          rect.setAttribute("height", hoverHeight);
          rect.setAttribute("fill", "#E9E9E9");

          innerChart.insertBefore(rect, innerChart.firstChild);
        }
      }
    },
    [labelHeight, totalPadding, labelPrefix],
  );

  const removeHoverBackground = useCallback(() => {
    addHoverBackground(null, null);
  }, [addHoverBackground]);

  const options = useMemo(
    () => ({
      ...parentOptions, // Merge parent options
      chart: {
        ...parentOptions?.chart,
        type: "bar",
        height: chartHeight,
        stacked: parentOptions?.chart?.stacked, // Preserve stacked
        sparkline: {
          enabled: true,
        },
        parentHeightOffset: 0,
        toolbar: {
          show: false,
        },
        events: {
          ...parentOptions?.chart?.events, // Preserve other events if any
          mouseMove: (event, chartContext, config) => {
            const dataPointIndex = config.dataPointIndex;
            if (
              dataPointIndex >= 0 &&
              dataPointIndex !== hoveredIndexRef.current
            ) {
              hoveredIndexRef.current = dataPointIndex;
              addHoverBackground(dataPointIndex, chartContext);
            }
          },
          mouseLeave: () => {
            hoveredIndexRef.current = null;
            removeHoverBackground();
          },
          dataPointSelection: parentOptions?.chart?.events?.dataPointSelection,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          horizontal: true, // Always horizontal for this component
          barHeight: `${fixedBarHeightProp}px`,
          distributed: false,
          ...parentOptions?.plotOptions?.bar, // Override if parent wants
        },
      },
      dataLabels: {
        enabled: false, // Default false usually
        ...parentOptions?.dataLabels,
      },
      grid: {
        show: true,
        padding: {
          top: 0,
          bottom: extraBottomPadding,
          left: 0,
          right: 0,
        },
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      xaxis: {
        ...parentOptions?.xaxis,
        categories: categories, // Use prop
        min: 0,
        max: xaxisMax, // Use calc or parent
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: false,
        },
      },
      yaxis: {
        show: false,
        ...parentOptions?.yaxis,
      },
      tooltip: {
        ...parentOptions?.tooltip,
        enabled: true,
        shared: true,
        intersect: false,
        followCursor: true,
      },
    }),
    [
      categories,
      chartHeight,
      addHoverBackground,
      removeHoverBackground,
      xaxisMax,
      parentOptions,
      extraBottomPadding,
    ],
  );

  const chartSeries = useMemo(() => series, [series]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ChartContainer>
        <LabelsContainer
          onMouseLeave={() => {
            hoveredIndexRef.current = null;
            removeHoverBackground();
          }}
          sx={{
            paddingTop: `${totalPadding / 2 + labelOffset}px`,
            paddingBottom: `${totalPadding / 2}px`,
          }}
        >
          {categories.map((category, index) => (
            <Box
              key={index}
              onMouseEnter={() => {
                if (hoveredIndexRef.current !== index) {
                  hoveredIndexRef.current = index;
                  addHoverBackground(index);
                }
              }}
              sx={{
                height: `${labelHeight}px`,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Box
                id={`${labelPrefix}-${index}`}
                sx={{
                  height: `${labelHeight}px`,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "transparent",
                  borderRadius: "4px",
                  transition: "background-color 0.1s ease",
                }}
              >
                <PrimaryTooltip title={category} placement="bottom">
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      lineHeight: "16px",
                      color: "#494949",
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      cursor: "pointer",
                    }}
                  >
                    {category}
                  </Typography>
                </PrimaryTooltip>
              </Box>
            </Box>
          ))}
        </LabelsContainer>
        <ChartWrapper drillDownEnabled={drillDownEnabled}>
          <Chart
            options={options}
            series={chartSeries}
            type="bar"
            height={chartHeight}
          />
        </ChartWrapper>
      </ChartContainer>

      <Box
        sx={{
          display: "flex",
          gap: "16px",
          width: "100%",
          paddingRight: SCROLLBAR_WIDTH,
        }}
      >
        <Box sx={{ width: "180px", flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            position: "relative",
            height: "30px",
            borderTop: "1px solid #E0E0E0",
          }}
        >
          {ticks.map((tick, index) => (
            <Box
              key={tick}
              sx={{
                position: "absolute",
                left: `${(tick / xaxisMax) * 100}%`,
                transform: "translateX(-50%)",
                top: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  lineHeight: "24px",
                  color: "#8F8F8F",
                  whiteSpace: "nowrap",
                }}
              >
                {xAxisTickFormatter ? xAxisTickFormatter(tick) : tick}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {(xAxisTitle || "Number of Vehicles") && (
        <Box
          sx={{ display: "flex", width: "100%", paddingRight: SCROLLBAR_WIDTH }}
        >
          <Box sx={{ width: "180px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{ fontSize: "12px", color: "#686868", fontWeight: 500 }}
            >
              {xAxisTitle || "Number of Vehicles"}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FleetBreakdownChartInternal;
